import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import { audioManager } from '../utils/audioManager';

interface SoundContextType {
  playSound: (soundName: string) => void;
  enableAudio: () => void;
}

const SoundContext = createContext<SoundContextType | undefined>(undefined);

export const SoundProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const hasInteracted = useRef(false);

  const enableAudio = useCallback(() => {
    if (!hasInteracted.current) {
      audioManager.resumeAudioContext();
      hasInteracted.current = true;
    }
  }, []);

  const playSound = useCallback((soundName: string) => {
    enableAudio();
    audioManager.playSound(soundName);
  }, [enableAudio]);

  useEffect(() => {
    const handleFirstInteraction = () => {
      enableAudio();
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('touchstart', handleFirstInteraction);
    };

    document.addEventListener('click', handleFirstInteraction);
    document.addEventListener('touchstart', handleFirstInteraction);

    return () => {
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('touchstart', handleFirstInteraction);
    };
  }, [enableAudio]);

  return (
    <SoundContext.Provider value={{ playSound, enableAudio }}>
      {children}
    </SoundContext.Provider>
  );
};

export const useSoundContext = (): SoundContextType => {
  const ctx = useContext(SoundContext);
  if (!ctx) throw new Error('useSoundContext debe usarse dentro de SoundProvider');
  return ctx;
};
